/**
 * CsvDownload Component
 * 
 * Component for downloading office employee salary and bonus data as CSV files.
 * Provides filtering options to download specific data sets based on:
 * - Data type (salary/bonus)
 * - Year
 * - Month (for salary) or Period (for bonus)
 * - Employee code
 * 
 * Features:
 * - Data type toggle (salary/bonus)
 * - Year selection (current year and 5 years back)
 * - Month selection for salary (includes "all months" option)
 * - Period selection for bonus (includes "all periods" option)
 * - Employee selection from available employees
 * - At least one filter required for download
 * - Loading state management
 * - Success/error message display with auto-dismiss
 * - CSV file download using FileUploadService
 * 
 * Used by:
 * - SalaryDataMain component for office employee data download
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import { downloadCsv, CsvDownloadOptions, fetchEmployees, Employee } from '../../../../services/FileUploadService';
import '../../css/CsvDownload.css';

/**
 * CsvDownload Component
 * 
 * Main component for downloading office employee salary and bonus CSV files.
 * Manages filter state and download operations.
 * 
 * @returns React functional component
 */
export const CsvDownload = () => {
  /**
   * State management:
   * - selectedDataType: Current data type ('salary' or 'bonus')
   * - selectedYear: Selected year filter (empty string = not selected)
   * - selectedMonth: Selected month filter for salary (empty string = not selected)
   * - selectedPeriod: Selected period filter for bonus (empty string = not selected)
   * - selectedEmployee: Selected employee code filter (empty string = all employees)
   * - isLoading: Loading state for download operation
   * - downloadMessage: Success or error message to display
   * - availableEmployees: Array of available employees from backend
   */
  const [selectedDataType, setSelectedDataType] = useState<'salary' | 'bonus'>('salary');
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [downloadMessage, setDownloadMessage] = useState<string>('');
  const [availableEmployees, setAvailableEmployees] = useState<Employee[]>([]);

  /**
   * Generate years (current year and 5 years back).
   * Creates array of years for selection dropdown.
   * 
   * @example
   * If current year is 2024, generates: [2024, 2023, 2022, 2021, 2020, 2019]
   */
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  /**
   * Generate months with "all" option.
   * Array of month options for salary data type, including "全月" (all months).
   */
  const months = [
    { value: 'all', label: '全月' },
    { value: '1', label: '1月' },
    { value: '2', label: '2月' },
    { value: '3', label: '3月' },
    { value: '4', label: '4月' },
    { value: '5', label: '5月' },
    { value: '6', label: '6月' },
    { value: '7', label: '7月' },
    { value: '8', label: '8月' },
    { value: '9', label: '9月' },
    { value: '10', label: '10月' },
    { value: '11', label: '11月' },
    { value: '12', label: '12月' }
  ];

  /**
   * Bonus periods - using month numbers.
   * Array of period options for bonus data type, including "全期間" (all periods).
   * Note: Bonus periods use month numbers (1-12) similar to salary months.
   */
  const bonusPeriods = [
    { value: 'all', label: '全期間' },
    { value: '1', label: '1月' },
    { value: '2', label: '2月' },
    { value: '3', label: '3月' },
    { value: '4', label: '4月' },
    { value: '5', label: '5月' },
    { value: '6', label: '6月' },
    { value: '7', label: '7月' },
    { value: '8', label: '8月' },
    { value: '9', label: '9月' },
    { value: '10', label: '10月' },
    { value: '11', label: '11月' },
    { value: '12', label: '12月' }
  ];

  /**
   * Load employees from backend on mount.
   * 
   * Fetches available employees from FileUploadService to populate
   * the employee selection dropdown.
   * 
   * @effect
   * Runs once on mount.
   */
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const employees = await fetchEmployees();
        setAvailableEmployees(employees);
      } catch (error) {
        console.error('Failed to load employees:', error);
        // Fallback to empty array
        setAvailableEmployees([]);
      }
    };

    loadEmployees();
  }, []);

  /**
   * Reset month/period when data type changes.
   * 
   * When switching between salary and bonus, clears the month/period
   * selections since they are specific to each data type.
   * Also clears any download messages.
   * 
   * @effect
   * Runs when selectedDataType changes.
   */
  useEffect(() => {
    // Reset month/period when data type changes
    setSelectedMonth('');
    setSelectedPeriod('');
    setDownloadMessage('');
  }, [selectedDataType]);

  /**
   * Auto-dismiss message after 5 seconds.
   * 
   * Automatically clears download messages (success or error) after 5 seconds.
   * Provides cleanup function to clear timer on unmount or message change.
   * 
   * @effect
   * Runs when downloadMessage changes.
   */
  useEffect(() => {
    if (downloadMessage) {
      const timer = setTimeout(() => {
        setDownloadMessage('');
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [downloadMessage]);


  /**
   * Handles CSV download operation.
   * 
   * Validates that at least one filter is selected, then builds download options
   * and calls FileUploadService to download CSV file.
   * Displays success or error message after download.
   * 
   * Filter validation:
   * - At least one of: year, month/period, or employee must be selected
   * 
   * Download options:
   * - dataType: 'salary' or 'bonus'
   * - year: Parsed integer if selected
   * - month: Parsed integer if selected (salary only, ignores 'all')
   * - period: String if selected (bonus only, ignores 'all')
   * - employeeCode: String if selected, undefined otherwise
   * 
   * @example
   * ```typescript
   * // Download salary data for January 2024, employee EMP001
   * selectedDataType = 'salary';
   * selectedYear = '2024';
   * selectedMonth = '1';
   * selectedEmployee = 'EMP001';
   * await handleDownload();
   * ```
   */
  const handleDownload = async () => {
    /**
     * Check if at least one filter is selected.
     * Validates that user has selected at least year, month/period, or employee.
     */
    const hasYearFilter = selectedYear !== '';
    const hasMonthFilter = selectedMonth !== '';
    const hasPeriodFilter = selectedPeriod !== '';
    const hasEmployeeFilter = selectedEmployee !== '';

    if (!hasYearFilter && !hasMonthFilter && !hasPeriodFilter && !hasEmployeeFilter) {
      setDownloadMessage('少なくとも1つのフィルターを選択してください。');
      return;
    }

    setIsLoading(true);
    setDownloadMessage('');

    try {
      /**
       * Build download options object.
       * Starts with required dataType and optional employeeCode.
       */
      const options: CsvDownloadOptions = {
        dataType: selectedDataType,
        employeeCode: selectedEmployee || undefined
      };

      /**
       * Add year if selected (parsed to integer).
       */
      if (selectedYear) {
        options.year = parseInt(selectedYear);
      }

      /**
       * Add month/period if selected based on data type.
       * For salary: Add month (parsed to integer, ignores 'all')
       * For bonus: Add period (string, ignores 'all')
       */
      if (selectedDataType === 'salary') {
        if (selectedMonth && selectedMonth !== 'all') {
          options.month = parseInt(selectedMonth);
        }
      } else {
        if (selectedPeriod && selectedPeriod !== 'all') {
          options.period = selectedPeriod;
        }
      }

      /**
       * Call FileUploadService to download CSV file.
       */
      await downloadCsv(options);

      /**
       * Build success message with selected filters.
       * Includes data type label (給与/賞与), time label (year/month/period),
       * and employee name if selected.
       */
      const dataTypeLabel = selectedDataType === 'salary' ? '給与' : '賞与';
      
      /**
       * Build time label based on selected options.
       * Format: "YYYY年MM月" or "YYYY年全月" or "YYYY年" or empty
       */
      let timeLabel = '';
      if (selectedYear) {
        if (selectedDataType === 'salary') {
          if (selectedMonth) {
            timeLabel = selectedMonth === 'all' ? `${selectedYear}年全月` : `${selectedYear}年${selectedMonth}月`;
          } else {
            timeLabel = `${selectedYear}年`;
          }
        } else {
          if (selectedPeriod) {
            timeLabel = selectedPeriod === 'all' ? `${selectedYear}年全期間` : `${selectedYear}年${bonusPeriods.find(p => p.value === selectedPeriod)?.label}`;
          } else {
            timeLabel = `${selectedYear}年`;
          }
        }
      }
      
      /**
       * Build filter label with employee name if selected.
       */
      const filterLabel = selectedEmployee 
        ? ` (${availableEmployees.find(e => e.code === selectedEmployee)?.name})`
        : '';

      /**
       * Construct final success message.
       */
      const finalMessage = timeLabel 
        ? `${dataTypeLabel}データのダウンロードが完了しました。${timeLabel}${filterLabel}`
        : `${dataTypeLabel}データのダウンロードが完了しました。${filterLabel}`;
      
      setDownloadMessage(finalMessage);
    } catch (error) {
      /**
       * Handle download errors with user-friendly message.
       */
      setDownloadMessage(error instanceof Error ? error.message : 'ダウンロードに失敗しました。もう一度お試しください。');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Clears all filter selections and messages.
   * 
   * Resets component to initial state:
   * - dataType: 'salary'
   * - All filters: empty strings
   * - downloadMessage: empty string
   */
  const handleClearSelection = () => {
    setSelectedDataType('salary');
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedPeriod('');
    setSelectedEmployee('');
    setDownloadMessage('');
  };

  /**
   * Filtered employees list (currently all available employees).
   * Can be extended in the future to filter employees based on other criteria.
   */
  const filteredEmployees = availableEmployees;

  /**
   * Check if any filter is selected.
   * Used to enable/disable download button.
   * At least one filter must be selected to download.
   */
  const hasAnyFilter = selectedYear !== '' || 
                      selectedMonth !== '' || 
                      selectedPeriod !== '' || 
                      selectedEmployee !== '';

  return (
    <div className="csv-download-container">
      {/* Download Message Display - Shows success/error messages */}
      {downloadMessage && (
        <div className={`message ${downloadMessage.includes('失敗') || downloadMessage.includes('見つかりません') ? 'error' : 'success'}`}>
          {/* Message icon - ❌ for errors, ✅ for success */}
          <span className="message-icon">
            {downloadMessage.includes('失敗') || downloadMessage.includes('見つかりません') ? '❌' : '✅'}
          </span>
          {/* Message text */}
          <span className="message-text">{downloadMessage}</span>
          {/* Close button - Dismisses message */}
          <button 
            className="message-close-btn"
            onClick={() => setDownloadMessage('')}
            aria-label="閉じる"
          >
            ×
          </button>
        </div>
      )}

      {/* Download Options Section */}
      <div className="download-options">
        <div className="option-card">
          {/* Instructions - At least one filter is required */}
          <p>選択した条件（少なくとも1つのフィルターが必要）のデータをCSV形式でダウンロードします</p>
          
          <div className="selection-group">
            {/* Left Column - Basic Settings */}
            <div className="selection-column">
              <h4>基本設定</h4>
              
              {/* Data Type Selection - Toggle between Salary (給与) and Bonus (賞与) */}
              <div className="select-wrapper">
                <label htmlFor="data-type-select">データ種別:</label>
                <select
                  id="data-type-select"
                  value={selectedDataType}
                  onChange={(e) => setSelectedDataType(e.target.value as 'salary' | 'bonus')}
                  disabled={isLoading}
                >
                  <option value="salary">給与データ</option>
                  <option value="bonus">賞与データ</option>
                </select>
              </div>

              {/* Year Selection - Optional filter */}
              <div className="select-wrapper">
                <label htmlFor="year-select">年 (オプション):</label>
                <select
                  id="year-select"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="">年を選択</option>
                  {/* Generate year options from current year back 5 years */}
                  {years.map(year => (
                    <option key={year} value={year}>{year}年</option>
                  ))}
                </select>
              </div>

              {/* Month/Period Selection - Conditional based on data type */}
              {selectedDataType === 'salary' ? (
                /**
                 * Month Selection - Only shown for salary data type.
                 * Includes "全月" (all months) option.
                 */
                <div className="select-wrapper">
                  <label htmlFor="month-select">月 (オプション):</label>
                  <select
                    id="month-select"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    disabled={isLoading}
                  >
                    <option value="">月を選択</option>
                    {months.map(month => (
                      <option key={month.value} value={month.value}>{month.label}</option>
                    ))}
                  </select>
                </div>
              ) : (
                /**
                 * Period Selection - Only shown for bonus data type.
                 * Includes "全期間" (all periods) option.
                 */
                <div className="select-wrapper">
                  <label htmlFor="period-select">期間 (オプション):</label>
                  <select
                    id="period-select"
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                    disabled={isLoading}
                  >
                    <option value="">期間を選択</option>
                    {bonusPeriods.map(period => (
                      <option key={period.value} value={period.value}>{period.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Right Column - Filter Settings */}
            <div className="selection-column">
              <h4>フィルター設定</h4>
              
              {/* Employee Selection - Optional filter */}
              <div className="select-wrapper">
                <label htmlFor="employee-select">従業員 (オプション):</label>
                <select
                  id="employee-select"
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="">全従業員</option>
                  {/* Generate employee options from available employees */}
                  {filteredEmployees.map(emp => (
                    <option key={emp.code} value={emp.code}>{emp.code} - {emp.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Download Button - Disabled when loading or no filters selected */}
          <button
            className="download-btn primary"
            onClick={handleDownload}
            disabled={isLoading || !hasAnyFilter}
          >
            {isLoading ? 'ダウンロード中...' : 'CSVダウンロード'}
          </button>
        </div>
      </div>

      {/* Action Buttons Section */}
      <div className="action-buttons">
        {/* Clear Selection Button - Resets all filters */}
        <button
          className="clear-btn"
          onClick={handleClearSelection}
          disabled={isLoading}
        >
          選択クリア
        </button>
      </div>
    </div>
  );
}; 